<script setup>
import { computed, onMounted } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAilab } from 'dashboard/composables/useAilab';
import { format } from 'date-fns';

const { currentAccount } = useAccount();
const {
  ailabEnabled,
  ailabLimits,
  documentLimits,
  responseLimits,
  fetchLimits,
} = useAilab();

const uiFlags = useMapGetter('accounts/getUIFlags');
const store = useStore();

const customAttributes = computed(() => {
  return currentAccount.value.custom_attributes || {};
});

const planName = computed(() => customAttributes.value.plan_name);
const subscribedQuantity = computed(
  () => customAttributes.value.subscribed_quantity
);

const subscriptionRenewsOn = computed(() => {
  if (!customAttributes.value.subscription_ends_on) return '';
  const endDate = new Date(customAttributes.value.subscription_ends_on);
  return format(endDate, 'dd MMM, yyyy');
});

const hasABillingPlan = computed(() => !!planName.value);

const fetchAccountDetails = async () => {
  if (!hasABillingPlan.value) {
    store.dispatch('accounts/subscription');
    fetchLimits();
  }
};

const onClickBillingPortal = () => {
  store.dispatch('accounts/checkout');
};

const onToggleChatWindow = () => {
  if (window.$chatwoot) {
    window.$chatwoot.toggle();
  }
};

const getUsagePercent = (consumed, total) => {
  if (!total) return 0;
  return Math.round((consumed / total) * 100);
};

const getUsageColor = percent => {
  if (percent < 50)
    return 'background: linear-gradient(to right, #10b981, #14b8a6)';
  if (percent < 80)
    return 'background: linear-gradient(to right, #f59e0b, #0066ff)';
  return 'background: linear-gradient(to right, #ef4444, #f43f5e)';
};

onMounted(fetchAccountDetails);
</script>

<template>
  <div class="billing-page flex flex-col w-full h-full overflow-hidden">
    <!-- Header -->
    <header
      class="page-header flex items-center justify-between px-6 flex-shrink-0"
      style="height: 50px"
    >
      <div class="flex items-center gap-3">
        <div class="header-icon">
          <span class="i-ph-credit-card-bold size-4" style="color: #6b7280" />
        </div>
        <h1 class="text-lg font-semibold" style="color: #111827">
          {{ $t('BILLING_SETTINGS.TITLE') }}
        </h1>
      </div>
      <button
        v-if="hasABillingPlan"
        class="primary-action-btn"
        @click="onClickBillingPortal"
      >
        <span class="i-ph-gear-six-bold size-4" />
        {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.BUTTON_TXT') }}
      </button>
    </header>

    <!-- Description -->
    <div
      class="px-6 py-3"
      style="border-bottom: 1px solid #e5e7eb; background: #fff"
    >
      <p class="text-sm" style="color: #4b5563">
        {{ $t('BILLING_SETTINGS.DESCRIPTION') }}
      </p>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto p-6" style="background: #fff">
      <!-- Loading -->
      <div
        v-if="uiFlags.isFetchingItem"
        class="flex items-center justify-center py-20"
      >
        <div class="loading-spinner" />
      </div>

      <!-- No Billing Plan -->
      <div v-else-if="!hasABillingPlan" class="empty-state">
        <div class="empty-icon-wrap">
          <span class="i-ph-credit-card-bold size-12" />
        </div>
        <h3 class="text-xl font-semibold mb-2" style="color: #111827">
          {{ $t('BILLING_SETTINGS.NO_BILLING_USER') }}
        </h3>
        <p class="text-n-slate-11 text-sm mb-6">
          Set up your billing to unlock premium features
        </p>
        <button class="primary-action-btn" @click="onClickBillingPortal">
          <span class="i-ph-plus-bold size-4" />
          Set Up Billing
        </button>
      </div>

      <!-- Billing Content -->
      <div v-else class="space-y-6">
        <!-- Current Plan Card -->
        <div class="billing-card">
          <div class="card-header">
            <div class="flex items-center gap-3">
              <div
                class="card-icon bg-gradient-to-br from-violet-500/20 to-purple-500/20"
              >
                <span class="i-ph-crown-bold size-5 text-violet-400" />
              </div>
              <div>
                <h3 class="text-base font-semibold" style="color: #111827">
                  {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.TITLE') }}
                </h3>
                <p class="text-sm text-n-slate-11 mt-0.5">
                  {{ $t('BILLING_SETTINGS.MANAGE_SUBSCRIPTION.DESCRIPTION') }}
                </p>
              </div>
            </div>
            <button class="secondary-action-btn" @click="onClickBillingPortal">
              <span class="i-ph-pencil-simple-bold size-4" />
              Manage
            </button>
          </div>

          <!-- Plan Details -->
          <div
            v-if="planName || subscribedQuantity || subscriptionRenewsOn"
            class="card-body"
          >
            <div class="stats-grid">
              <div v-if="planName" class="stat-item">
                <span class="stat-label">{{
                  $t('BILLING_SETTINGS.CURRENT_PLAN.TITLE')
                }}</span>
                <span class="stat-value">{{ planName }}</span>
              </div>
              <div v-if="subscribedQuantity" class="stat-item">
                <span class="stat-label">{{
                  $t('BILLING_SETTINGS.CURRENT_PLAN.SEAT_COUNT')
                }}</span>
                <span class="stat-value">{{ subscribedQuantity }}</span>
              </div>
              <div v-if="subscriptionRenewsOn" class="stat-item">
                <span class="stat-label">{{
                  $t('BILLING_SETTINGS.CURRENT_PLAN.RENEWS_ON')
                }}</span>
                <span class="stat-value">{{ subscriptionRenewsOn }}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- AiLab Card -->
        <div class="billing-card">
          <div class="card-header">
            <div class="flex items-center gap-3">
              <div
                class="card-icon"
                style="
                  background: linear-gradient(
                    to bottom right,
                    rgba(245, 158, 11, 0.2),
                    rgba(0, 102, 255, 0.2)
                  );
                "
              >
                <span class="i-ph-sparkle-bold size-5 text-amber-400" />
              </div>
              <div>
                <h3 class="text-base font-semibold" style="color: #111827">
                  {{ $t('BILLING_SETTINGS.AILAB.TITLE') }}
                </h3>
                <p class="text-sm text-n-slate-11 mt-0.5">
                  {{
                    ailabEnabled
                      ? $t('BILLING_SETTINGS.AILAB.DESCRIPTION')
                      : $t('BILLING_SETTINGS.AILAB.UPGRADE')
                  }}
                </p>
              </div>
            </div>
            <button
              v-if="!ailabEnabled"
              class="primary-action-btn"
              @click="onClickBillingPortal"
            >
              <span class="i-ph-rocket-launch-bold size-4" />
              {{ $t('AILAB.PAYWALL.UPGRADE_NOW') }}
            </button>
          </div>

          <!-- AiLab Usage Meters -->
          <div v-if="ailabEnabled && ailabLimits" class="card-body">
            <div class="space-y-4">
              <!-- Responses Meter -->
              <div v-if="responseLimits" class="usage-meter">
                <div class="meter-header">
                  <span class="meter-title">{{
                    $t('BILLING_SETTINGS.AILAB.RESPONSES')
                  }}</span>
                  <span class="meter-count"
                    >{{ responseLimits.consumed }} /
                    {{ responseLimits.totalCount }}</span
                  >
                </div>
                <div class="meter-bar">
                  <div
                    class="meter-fill"
                    :style="`width: ${getUsagePercent(responseLimits.consumed, responseLimits.totalCount)}%; ${getUsageColor(getUsagePercent(responseLimits.consumed, responseLimits.totalCount))}`"
                  />
                </div>
              </div>

              <!-- Documents Meter -->
              <div v-if="documentLimits" class="usage-meter">
                <div class="meter-header">
                  <span class="meter-title">{{
                    $t('BILLING_SETTINGS.AILAB.DOCUMENTS')
                  }}</span>
                  <span class="meter-count"
                    >{{ documentLimits.consumed }} /
                    {{ documentLimits.totalCount }}</span
                  >
                </div>
                <div class="meter-bar">
                  <div
                    class="meter-fill"
                    :style="`width: ${getUsagePercent(documentLimits.consumed, documentLimits.totalCount)}%; ${getUsageColor(getUsagePercent(documentLimits.consumed, documentLimits.totalCount))}`"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Support Card -->
        <div class="billing-card support-card">
          <div class="card-header">
            <div class="flex items-center gap-3">
              <div
                class="card-icon bg-gradient-to-br from-blue-500/20 to-cyan-500/20"
              >
                <span class="i-ph-headset-bold size-5 text-blue-400" />
              </div>
              <div>
                <h3 class="text-base font-semibold" style="color: #111827">
                  {{ $t('BILLING_SETTINGS.CHAT_WITH_US.TITLE') }}
                </h3>
                <p class="text-sm text-n-slate-11 mt-0.5">
                  {{ $t('BILLING_SETTINGS.CHAT_WITH_US.DESCRIPTION') }}
                </p>
              </div>
            </div>
            <button class="secondary-action-btn" @click="onToggleChatWindow">
              <span class="i-ph-chat-circle-dots-bold size-4" />
              {{ $t('BILLING_SETTINGS.CHAT_WITH_US.BUTTON_TXT') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Forced white theme */
.billing-page,
.dark .billing-page {
  background: #ffffff !important;
}

.page-header {
  background: #ffffff !important;
  border-bottom: 1px solid #e5e7eb !important;
}

.header-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
}

.primary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  background: #111827;
  color: #ffffff;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.primary-action-btn:hover {
  background: #1f2937;
}

.secondary-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  background: #f9fafb;
  color: #374151;
  border: 1px solid #e5e7eb;
  border-radius: 9999px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.secondary-action-btn:hover {
  background: #f3f4f6;
  color: #111827;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #e5e7eb;
  border-top-color: #6b7280;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon-wrap {
  width: 100px;
  height: 100px;
  border-radius: 24px;
  background: #f3f4f6;
  border: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  margin-bottom: 24px;
}

.billing-card {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.2s ease;
}

.billing-card:hover {
  border-color: #d1d5db;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
}

.card-icon {
  width: 44px;
  height: 44px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.card-body {
  padding: 0 24px 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 24px;
  padding: 20px 24px;
  background: #f9fafb;
  border-radius: 12px;
}

.stat-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.stat-label {
  font-size: 11px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #9ca3af;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
}

.usage-meter {
  background: #f9fafb;
  padding: 16px 20px;
  border-radius: 12px;
}

.meter-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 10px;
}

.meter-title {
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6b7280;
}

.meter-count {
  font-size: 12px;
  font-weight: 600;
  color: #374151;
  font-variant-numeric: tabular-nums;
}

.meter-bar {
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  overflow: hidden;
}

.meter-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease;
}

.support-card {
  background: #f0f9ff;
  border-color: #bae6fd;
}

.support-card:hover {
  border-color: #7dd3fc;
}

:deep(.text-n-slate-12) {
  color: #111827 !important;
}
:deep(.text-n-slate-11) {
  color: #4b5563 !important;
}
:deep(.text-n-slate-10) {
  color: #6b7280 !important;
}
:deep(.bg-n-background),
:deep(.bg-n-solid-1),
:deep(.bg-n-solid-2) {
  background: #ffffff !important;
}
:deep(.border-n-weak) {
  border-color: #e5e7eb !important;
}
</style>
